import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Loader } from "@/components/ui/Loader";
import { Select } from "@/components/ui/Select";
import { ROUTES } from "@/config/constants";
import { cn } from "@/utils/cn";
import {
  extractGgError,
  fetchCicloDetalhe,
  fetchResultadosLideranca,
  setStatusCicloApi,
  updateCicloApi,
  type GgCicloDetalheDto,
  type GgCompetenciaDto,
  type GgConviteLiderancaDto,
  type GgLiderancaResultadosDto,
  type GgParLiderancaDto,
  type GgPerguntaLiderancaDto,
} from "@/services/genteGestao";
import { AbaLiderancaPerguntas } from "./AbaLiderancaPerguntas";
import { AbaLiderancaParticipantes } from "./AbaLiderancaParticipantes";
import { AbaLiderancaResultados } from "./AbaLiderancaResultados";

type Aba = "perguntas" | "participantes" | "resultados";

export function GenteGestaoLiderancaDetalhePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [aba, setAba] = useState<Aba>("participantes");
  const [ciclo, setCiclo] = useState<GgCicloDetalheDto | null>(null);
  const [perguntas, setPerguntas] = useState<GgPerguntaLiderancaDto[]>([]);
  const [convites, setConvites] = useState<GgConviteLiderancaDto[]>([]);
  const [competencias, setCompetencias] = useState<GgCompetenciaDto[]>([]);
  const [pares, setPares] = useState<GgParLiderancaDto[]>([]);
  const [resultados, setResultados] = useState<GgLiderancaResultadosDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const det = await fetchCicloDetalhe(id);
      setCiclo(det.ciclo);
      setPerguntas(det.perguntas);
      setConvites(det.convites);
      setCompetencias(det.competencias);
      setPares(det.pares);
      if (det.ciclo.respostas > 0) {
        setResultados(await fetchResultadosLideranca(id));
      } else {
        setResultados(null);
      }
    } catch (e) {
      setError(extractGgError(e, "Não foi possível carregar o ciclo."));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading && !ciclo) {
    return (
      <div className="flex justify-center py-16">
        <Loader label="Carregando ciclo..." />
      </div>
    );
  }
  if (error || !ciclo) {
    return <p className="text-sm text-red-600">{error ?? "Ciclo não encontrado."}</p>;
  }

  const current = ciclo;

  async function salvarMeta() {
    setSaving(true);
    try {
      await updateCicloApi(current.id, {
        titulo: current.titulo,
        descricao: current.descricao,
        nomeEmpresa: current.nomeEmpresa,
        dataInicio: current.dataInicio,
        dataFim: current.dataFim,
        mensagemConvite: current.mensagemConvite,
        modoPrivacidade: current.modoPrivacidade,
        minRespondentes: current.minRespondentes,
      });
      await load();
    } catch (e) {
      setError(extractGgError(e, "Não foi possível salvar."));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <button className="text-sm text-brand-700 hover:underline" onClick={() => navigate(ROUTES.GENTE_GESTAO_LIDERANCA)}>
          ← Ciclos
        </button>
        <div className="flex flex-wrap items-center gap-2">
          <span
            className={cn(
              "rounded-full px-2.5 py-1 text-xs font-semibold",
              ciclo.status === "ATIVA"
                ? "bg-emerald-50 text-emerald-800"
                : ciclo.status === "ENCERRADA"
                  ? "bg-slate-100 text-slate-600"
                  : "bg-amber-50 text-amber-800",
            )}
          >
            {ciclo.status === "ATIVA" ? "Ativa" : ciclo.status === "ENCERRADA" ? "Encerrada" : "Rascunho"}
          </span>
          {ciclo.status !== "ATIVA" && (
            <Button size="sm" onClick={() => void setStatusCicloApi(ciclo.id, "ATIVA").then(load)}>
              Ativar ciclo
            </Button>
          )}
          {ciclo.status === "ATIVA" && (
            <Button size="sm" variant="secondary" onClick={() => void setStatusCicloApi(ciclo.id, "ENCERRADA").then(load)}>
              Encerrar
            </Button>
          )}
          {ciclo.status === "ENCERRADA" && (
            <Button size="sm" variant="secondary" onClick={() => void setStatusCicloApi(ciclo.id, "ATIVA").then(load)}>
              Reabrir
            </Button>
          )}
        </div>
      </div>

      <div className="grid gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-card sm:grid-cols-2">
        <Input label="Título" value={current.titulo} onChange={(e) => setCiclo({ ...current, titulo: e.target.value })} />
        <Input
          label="Empresa"
          value={current.nomeEmpresa}
          onChange={(e) => setCiclo({ ...current, nomeEmpresa: e.target.value })}
        />
        <Input
          label="Início"
          type="date"
          value={current.dataInicio?.slice(0, 10) ?? ""}
          onChange={(e) => setCiclo({ ...current, dataInicio: e.target.value || null })}
        />
        <Input
          label="Fim"
          type="date"
          value={current.dataFim?.slice(0, 10) ?? ""}
          onChange={(e) => setCiclo({ ...current, dataFim: e.target.value || null })}
        />
        <Select
          label="Privacidade"
          value={current.modoPrivacidade}
          onChange={(e) =>
            setCiclo({ ...current, modoPrivacidade: e.target.value as GgCicloDetalheDto["modoPrivacidade"] })
          }
          options={[
            { value: "CONFIDENCIAL", label: "Confidencial" },
            { value: "IDENTIFICADA", label: "Identificada" },
          ]}
        />
        <Input
          label="Mín. respondentes"
          type="number"
          min={1}
          value={String(current.minRespondentes)}
          onChange={(e) => setCiclo({ ...current, minRespondentes: Number(e.target.value) || 3 })}
        />
        <Button size="sm" variant="secondary" loading={saving} onClick={() => void salvarMeta()}>
          Salvar dados
        </Button>
      </div>

      <div className="flex flex-wrap gap-2">
        {(
          [
            ["perguntas", "Perguntas"],
            ["participantes", "Participantes e envio"],
            ["resultados", "Resultados"],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setAba(key)}
            className={cn(
              "rounded-lg px-3 py-2 text-sm font-medium",
              aba === key ? "bg-brand-600 text-white" : "bg-white border border-slate-200 text-slate-600",
            )}
          >
            {label}
          </button>
        ))}
      </div>

      {aba === "perguntas" && (
        <AbaLiderancaPerguntas
          cicloId={ciclo.id}
          perguntas={perguntas}
          competencias={competencias}
          bloqueado={ciclo.perguntasBloqueadas}
          onChanged={() => void load()}
        />
      )}
      {aba === "participantes" && (
        <AbaLiderancaParticipantes ciclo={ciclo} convites={convites} pares={pares} onChanged={() => void load()} />
      )}
      {aba === "resultados" &&
        (resultados ? (
          <AbaLiderancaResultados resultados={resultados} />
        ) : (
          <p className="text-sm text-slate-500">Ainda não há respostas para calcular os resultados.</p>
        ))}
    </div>
  );
}
